import React, { useCallback, useMemo, useState } from 'react';
import { dashboardTranslations, NavLabelKey } from '../../config/dashboardTranslations';
import { useLanguage } from '../../context/LanguageContext';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LayoutDashboard,
  MessageCircle,
  GraduationCap,
  Target,
  CalendarCheck,
  Trophy,
  CreditCard,
  LogOut,
  Menu,
  UserRound,
  Zap,
  Moon,
  Sun,
  Crown,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../../services/authService';
import { getCurrentTokenUsage } from '../../services/tokenUsageService';
import { useSubscription } from '../../hooks/useSubscription';
import SubscriptionModal from '../subscription/SubscriptionModal';
import { useColorMode } from '../../context/ColorModeContext';
import logo from '../../assets/images/logo.jpeg';

const drawerWidth = 280;

type NavItem = {
  labelKey: NavLabelKey;
  path: string;
  icon: React.ReactNode;
  match?: (pathname: string, state?: any) => boolean;
  state?: any;
};

const primaryNav: NavItem[] = [
  {
    labelKey: 'overview',
    path: '/dashboard',
    icon: <LayoutDashboard size={20} />,
  },
  {
    labelKey: 'smartChat',
    path: '/chat',
    icon: <MessageCircle size={20} />,
    match: (pathname, state) =>
      pathname.startsWith('/chat') &&
      state?.chatMode !== 'zaptalk' &&
      state?.subject !== 'Mental Health',
  },
  {
    labelKey: 'examMode',
    path: '/exam-mode',
    icon: <Target size={20} />,
  },
  {
    labelKey: 'myExams',
    path: '/your-exams',
    icon: <CalendarCheck size={20} />,
    match: (pathname) => pathname.startsWith('/your-exams'),
  },
  {
    labelKey: 'mockStudio',
    path: '/mock',
    icon: <GraduationCap size={20} />,
    match: (pathname) => pathname.startsWith('/mock'),
  },
  {
    labelKey: 'scoreboard',
    path: '/score',
    icon: <Trophy size={20} />,
  },
  {
    labelKey: 'zapTalk',
    path: '/chat',
    icon: <Zap size={20} />,
    state: { subject: 'Mental Health', chatMode: 'zaptalk' },
    match: (pathname, state) =>
      pathname.startsWith('/chat') &&
      (state?.chatMode === 'zaptalk' || state?.subject === 'Mental Health'),
  },
  {
    labelKey: 'pricing',
    path: '/pricing',
    icon: <CreditCard size={20} />,
  },
];

const secondaryNav: NavItem[] = [
  {
    labelKey: 'profile',
    path: '/profile',
    icon: <UserRound size={20} />,
  },
  // {
  //   labelKey: 'settings',
  //   path: '/profile',
  //   icon: <Settings size={20} />,
  // },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const { mode, toggleMode } = useColorMode();
  const translations = dashboardTranslations[language];
  const { hasActiveSubscription, isModalOpen, openSubscriptionModal, closeSubscriptionModal } = useSubscription();

  const guardNavigation = useCallback(
    async (path: string, state?: any) => {
      const restricted = ['/mock', '/chat', '/your-exams'];
      if (restricted.some((restrictedPath) => path.startsWith(restrictedPath))) {
        // If user has active subscription, allow navigation
        if (hasActiveSubscription) {
          navigate(path, { state });
          setMobileOpen(false);
          return;
        }

        // For non-subscribed users, check token limits
        try {
          const usage = await getCurrentTokenUsage();
          if (usage.isLimitExceeded) {
            // Credits exhausted - show subscription modal
            openSubscriptionModal();
            setMobileOpen(false);
            return;
          }
          // Credits available - allow navigation
        } catch (error) {
          console.error('Failed to check token usage before navigation:', error);
          // On error, allow navigation (don't block user)
        }
      }

      navigate(path, { state });
      setMobileOpen(false);
    },
    [navigate, hasActiveSubscription, openSubscriptionModal],
  );

  const activeMatch = useMemo(
    () => (item: NavItem) =>
      item.match ? item.match(location.pathname, location.state) : location.pathname === item.path,
    [location.pathname, location.state],
  );

  const activePrimaryNavItem = primaryNav.find((item) => activeMatch(item));
  const headerTitle = activePrimaryNavItem
    ? translations.navLabels[activePrimaryNavItem.labelKey]
    : translations.brandName;

  const handleLogout = () => {
    setIsLogoutDialogOpen(true);
  };

  const confirmLogout = async () => {
    await logout();
    navigate('/');
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 3,
        gap: 3,
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(11, 18, 32, 0.92) 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #f6f7ff 100%)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar
          alt={translations.brandName}
          src={logo}
          sx={{
            width: 48,
            height: 48,
            bgcolor: 'transparent',
          }}
        />
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {translations.brandName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translations.workspaceLabel}
          </Typography>
        </Box>
      </Stack>


      <List sx={{ flexGrow: 1 }}>
        {primaryNav.map((item) => (
          <ListItemButton
            key={item.labelKey}
            selected={activeMatch(item)}
            onClick={() => {
              void guardNavigation(item.path, item.state);
            }}
            sx={{
              mb: 0.5,
              px: 2,
              py: 1.1,
              color: activeMatch(item) ? 'primary.dark' : 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(90, 75, 252, 0.12)',
                color: 'primary.dark',
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: 'inherit',
                minWidth: 42,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={translations.navLabels[item.labelKey]} />
          </ListItemButton>
        ))}
      </List>

      <Divider />

      <List>
        {secondaryNav.map((item) => (
          <ListItemButton
            key={item.path}
            onClick={() => void guardNavigation(item.path)}
            sx={{
              mb: 0.5,
              px: 2,
              py: 1,
              color: location.pathname === item.path ? 'primary.dark' : 'text.secondary',
            }}
          >
            <ListItemIcon
              sx={{
                color: 'inherit',
                minWidth: 42,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={translations.navLabels[item.labelKey]} />
          </ListItemButton>
        ))}
      </List>

      <Button
        variant="text"
        color="inherit"
        onClick={handleLogout}
        startIcon={<LogOut size={18} />}
        sx={{ justifyContent: 'flex-start', px: 1.5 }}
      >
        {translations.logout}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={(theme) => ({
          ml: { md: `${drawerWidth}px` },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.72 : 0.86),
          backdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.7 : 0.9)}`,
        })}
      >
        <Toolbar sx={{ minHeight: 72, px: { xs: 2, md: 4 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen((prev) => !prev)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <Menu size={22} />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {headerTitle}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton
              onClick={toggleMode}
              color="inherit"
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={(theme) => ({
                width: 44,
                height: 44,
                borderRadius: 999,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.5 : 0.7),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.65 : 0.9),
                },
              })}
            >
              {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </IconButton>
            {/* <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                px: 2,
                py: 0.8,
                borderRadius: 999,
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.3)}`,
                backgroundColor: (theme) =>
                  alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.5 : 0.75),
              }}
            >
              <Typography fontWeight={600} fontSize="0.9rem">
                {translations.language}
              </Typography>
              <Box
                component="select"
                value={language}
                onChange={(event) => setLanguage(event.target.value as SupportedLanguage)}
                sx={{
                  border: 'none',
                  background: 'transparent',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  outline: 'none',
                  cursor: 'pointer',
                  color: 'inherit',
                }}
              >
                {languageOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </Box>
            </Box> */}
            {hasActiveSubscription ? (
              <Button
                variant="outlined"
                color="warning"
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  pointerEvents: 'none',
                  gap: 1,
                  fontWeight: 'bold',
                }}
              >
                <Crown size={18} /> Pro
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/pricing')}
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              >
                {translations.upgradeButton}
              </Button>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label={translations.sidebarAriaLabel}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
              backgroundColor: 'transparent',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2.5, md: 4 },
          pt: { xs: 10, md: 12 },
          pb: 6,
          minHeight: '100vh',
          background: 'radial-gradient(circle at top right, rgba(90, 75, 252, 0.08), transparent)',
        }}
      >
        <Box maxWidth="xl" mx="auto" width="100%">
          {children}
        </Box>
      </Box>

      <Dialog open={isLogoutDialogOpen} onClose={() => setIsLogoutDialogOpen(false)}>
        <DialogTitle>{translations.logoutConfirmation.title}</DialogTitle>
        <DialogContent>
          {translations.logoutConfirmation.message}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsLogoutDialogOpen(false)}>{translations.logoutConfirmation.cancel}</Button>
          <Button onClick={confirmLogout} color="error" autoFocus>
            {translations.logoutConfirmation.confirm}
          </Button>
        </DialogActions>
      </Dialog>

      <SubscriptionModal
        isOpen={isModalOpen}
        onClose={closeSubscriptionModal}
        title="Upgrade to Pro"
        message="Subscribe to unlock all premium features including Mock Studio, Smart Chat, ZapTalk, Exam Mode, and My Exams!"
      />
    </Box>
  );
};

export default DashboardLayout;
